/** `plans.code` server-side: what the app keys on, never the display name. */
export type TPlanCode =
	"free" | "user" | "user_plus" | "team" | "team_plus" | "enterprise";

export type TPlanFamily = "free" | "user" | "team" | "enterprise";

export type TBillingInterval = "month" | "year";

/** Stripe's statuses, plus `expired` for a local trial that ran out. */
export type TSubscriptionStatus =
	| "trialing"
	| "active"
	| "past_due"
	| "canceled"
	| "unpaid"
	| "incomplete"
	| "incomplete_expired"
	| "paused"
	| "expired";

/**
 * What a plan can run out of — `details.limit` of a 402. `trial` and
 * `intent` are the front's own: the trial banner opening the upgrade
 * modal on the plan being tried, and the plan picked on the site before
 * signing up.
 */
export type TPlanLimit =
	| "projects"
	| "companies"
	| "clients"
	| "members"
	| "pdf"
	| "sharing"
	| "invoices"
	| "trial"
	| "intent";

/** PlanDTO. A `null` limit is unlimited; prices are in cents, for display. */
export type TPlan = {
	id: number;
	code: TPlanCode;
	name: string;
	family: TPlanFamily;
	tier: number;
	maxProjects: number | null;
	maxCompanies: number | null;
	maxClients: number | null;
	/** Members of the workspace, owners aside. */
	maxMembers: number | null;
	canExportPdf: boolean;
	canShare: boolean;
	canInvoice: boolean;
	priceMonth: number | null;
	priceYear: number | null;
	currency: string;
	isPublic: boolean;
};

/** SubscriptionDTO: what was subscribed to — the plan in force is `TSubscriptionState.plan`. */
export type TSubscription = {
	plan: TPlanCode;
	status: TSubscriptionStatus;
	interval: TBillingInterval | null;
	trialEndsAt: string | null;
	currentPeriodEnd: string | null;
	cancelAtPeriodEnd: boolean;
	/** Stripe knows the account: the billing portal can open. */
	hasCustomer: boolean;
};

/** A counted limit. `requiredPlan` is set once `used >= max`: the cheapest plan that takes one more. */
export type TUsage = {
	used: number;
	max: number | null;
	requiredPlan: TPlanCode | null;
};

/** A switched feature. `requiredPlan` is set when off: the cheapest plan that has it. */
export type TFeature = { enabled: boolean; requiredPlan: TPlanCode | null };

export type TUsageKey = Extract<
	TPlanLimit,
	"projects" | "companies" | "clients" | "members"
>;

export type TFeatureKey = Extract<TPlanLimit, "pdf" | "invoices" | "sharing">;

/** What a button can be gated on. */
export type TPlanGateKey = TUsageKey | TFeatureKey;

/**
 * `GET /subscription`: where the current workspace stands. The
 * catalogue is `GET /plans`. `canManage` is false for a member of
 * someone else's workspace: they work under its plan, the owner pays.
 */
export type TSubscriptionState = {
	plan: TPlan;
	subscription: TSubscription | null;
	usage: Record<TUsageKey, TUsage>;
	features: Record<TFeatureKey, TFeature>;
	workspaceId: number;
	canManage: boolean;
};

/** `details` of a `402 PLAN.LIMIT`. */
export type TPlanLimitDetails = {
	limit: TPlanLimit;
	plan: TPlanCode;
	max: number | null;
	used: number;
	/** The cheapest plan under which the action would go through. */
	requiredPlan: TPlanCode | null;
};

export const PLAN_LIMIT_CODE = "PLAN.LIMIT";

/**
 * Fired on `window` with `TPlanLimitDetails`: by the API client when a
 * request meets a 402, and by a locked button when it is clicked. The
 * upgrade modal listens.
 */
export const PLAN_LIMIT_EVENT = "plan:limit";

export type TPlanGate = {
	locked: boolean;
	requiredPlan: TPlanCode | null;
	/** The count, for a limit; `null` for a feature. */
	usage: TUsage | null;
};
